/**
 * useScraper Hook
 * 
 * React hook for scraper operations using ScraperRepository.
 * Provides loading states, error handling, and real-time updates.
 * 
 * Usage:
 * - Single scraper: useScraper(scraperId)
 * - Operations only: useScraper()
 */

import { useState, useEffect, useCallback } from 'react';
import { ScraperRepository } from '../models/ScraperRepository';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../firebase';

const repo = new ScraperRepository(db, functions);

export const useScraper = (scraperId = null) => {
  const [scraper, setScraper] = useState(null);
  const [loading, setLoading] = useState(!!scraperId);
  const [error, setError] = useState(null);

  // Fetch single scraper with real-time updates
  useEffect(() => {
    if (!scraperId) return;

    setLoading(true);
    setError(null);

    // Timeout guard: if onSnapshot never fires (stale Firestore channel), stop spinning after 15s
    const timeoutId = setTimeout(() => {
      setLoading((current) => {
        if (current) {
          console.warn('🔧 [NEW ARCH] useScraper: onSnapshot timed out for', scraperId);
          setError(new Error('Firestore connection timed out. Hard-refresh the page (Cmd+Shift+R) to fix.'));
          return false;
        }
        return current;
      });
    }, 15000);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'discovery_scrapers', scraperId),
      (doc) => {
        clearTimeout(timeoutId);
        if (doc.exists()) {
          setScraper({ id: doc.id, ...doc.data() });
          setError(null);
        } else {
          setScraper(null);
          setError(new Error('Scraper not found'));
        }
        setLoading(false);
      },
      (err) => {
        clearTimeout(timeoutId);
        console.error('Error fetching scraper:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [scraperId]);

  // Manual refresh
  const refresh = useCallback(async () => {
    if (!scraperId) return;

    setLoading(true);
    try {
      const scraperData = await repo.findById(scraperId);
      setScraper(scraperData);
      setError(null);
    } catch (err) {
      console.error('Error refreshing scraper:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [scraperId]);

  // Update scraper (via Cloud Function)
  const updateScraper = useCallback(async (updates) => {
    if (!scraperId) throw new Error('No scraper ID provided');
    
    try {
      await repo.update(scraperId, updates);
    } catch (err) {
      console.error('Error updating scraper:', err);
      throw err;
    }
  }, [scraperId]);

  // Delete scraper (via Cloud Function)
  const deleteScraper = useCallback(async () => {
    if (!scraperId) throw new Error('No scraper ID provided');
    
    try {
      await repo.delete(scraperId);
    } catch (err) {
      console.error('Error deleting scraper:', err);
      throw err;
    }
  }, [scraperId]);

  // Toggle availability
  const toggleAvailability = useCallback(async () => {
    if (!scraperId) throw new Error('No scraper ID provided');
    
    try {
      await repo.toggleAvailability(scraperId);
    } catch (err) {
      console.error('Error toggling availability:', err);
      throw err;
    }
  }, [scraperId]);

  // Update health status (direct update, not via Cloud Function)
  const updateHealthStatus = useCallback(async (healthData) => {
    if (!scraperId) throw new Error('No scraper ID provided');
    
    try {
      await repo.updateHealthStatus(scraperId, healthData);
    } catch (err) {
      console.error('Error updating health status:', err);
      throw err;
    }
  }, [scraperId]);

  // Update last used timestamp (direct update, not via Cloud Function)
  const updateLastUsed = useCallback(async () => {
    if (!scraperId) throw new Error('No scraper ID provided');
    
    try {
      await repo.updateLastUsed(scraperId);
    } catch (err) {
      console.error('Error updating last used:', err);
      throw err;
    }
  }, [scraperId]);

  // Count associated scrapes
  const countScrapes = useCallback(async () => {
    if (!scraper?.scraperId) return 0;
    
    try {
      const count = await repo.countScrapes(scraper.scraperId);
      return count;
    } catch (err) {
      console.error('Error counting scrapes:', err);
      throw err;
    }
  }, [scraper?.scraperId]);

  return {
    scraper,
    loading,
    error,
    refresh,
    updateScraper,
    deleteScraper,
    toggleAvailability,
    updateHealthStatus,
    updateLastUsed,
    countScrapes,
  };
};
